import React, { createContext, useContext, useRef, useEffect } from "react";

// Lets a routed page (currently just DigiTwin's BimViewer, for its loaded-models warning)
// intercept top-nav navigation clicks without Layout.js needing to know anything about
// DigiTwin specifically — Layout calls runGuard(proceed) instead of navigate() directly;
// runGuard either calls proceed() immediately (nothing registered, or the registered guard
// decides there's nothing to warn about) or shows its own confirmation first.
const NavGuardContext = createContext(null);

export function NavGuardProvider({ children }) {
  const guardRef = useRef(null);
  const setGuard = (fn) => { guardRef.current = fn; };
  const runGuard = (proceed) => {
    if (guardRef.current) guardRef.current(proceed);
    else proceed();
  };
  return (
    <NavGuardContext.Provider value={{ setGuard, runGuard }}>
      {children}
    </NavGuardContext.Provider>
  );
}

// Registers guardFn(proceed) as the active nav guard for as long as the calling component
// stays mounted (cleared automatically on unmount). guardFn must call proceed() once it's
// safe to navigate — immediately if there's nothing to warn about, or from a confirm dialog's
// onOk. Takes the latest guardFn on every render without re-registering on every change (the
// registered wrapper is stable — only mount/unmount touch the context).
export function useRegisterNavGuard(guardFn) {
  const ctx = useContext(NavGuardContext);
  const guardFnRef = useRef(guardFn);
  guardFnRef.current = guardFn;
  useEffect(() => {
    if (!ctx) return;
    ctx.setGuard((proceed) => guardFnRef.current(proceed));
    return () => ctx.setGuard(null);
  }, [ctx]);
}

// Layout.js's nav click handlers call this instead of navigate() directly.
export function useNavGuardRunner() {
  const ctx = useContext(NavGuardContext);
  return ctx ? ctx.runGuard : (proceed) => proceed();
}
